import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../utils/axiosConfig';
import { hora, fechaCorta, esHoy } from './formato';

const numero = (n) => (n == null ? '?' : Number(n).toLocaleString('es-VE'));

const cuando = (iso) => (esHoy(iso) ? `a las ${hora(iso)}` : `el ${fechaCorta(iso)} a las ${hora(iso)}`);

const ETIQUETAS = {
    usuarios: { accion: 'Traer usuarios', enCurso: 'Leyendo usuarios' },
    marcajes: { accion: 'Traer marcajes', enCurso: 'Leyendo marcajes' },
};

// Qué pasó con la última lectura de un tipo, en una línea
const resumen = (tipo, t) => {
    if (!t) return null;
    if (t.estado === 'en_curso') {
        if (tipo === 'usuarios') {
            return t.total ? `${numero(t.hechos)} de ${numero(t.total)} credenciales` : 'Pidiendo la lista';
        }
        return `${numero(t.hechos)} leídos, ${numero(t.nuevos)} nuevos`;
    }
    if (t.estado === 'cancelada') return `Cancelada ${cuando(t.terminado_en)}`;
    if (t.estado === 'error') return t.error || `Falló ${cuando(t.terminado_en)}`;
    const detalle = tipo === 'usuarios'
        ? `${numero(t.hechos)} credenciales leídas`
        : `${numero(t.hechos)} marcajes leídos, ${numero(t.nuevos)} nuevos`;
    return `${detalle} ${cuando(t.terminado_en)}${t.error ? `. ${t.error}` : ''}`;
};

export const Lectura = ({ aparato, tipo, onCambio }) => {
    const [enviando, setEnviando] = useState(false);
    const t = aparato.tareas?.[tipo];
    const enCurso = t?.estado === 'en_curso';
    const avance = enCurso && tipo === 'usuarios' && t.total ? Math.min(100, (t.hechos / t.total) * 100) : null;

    const pedir = async () => {
        setEnviando(true);
        try {
            await axiosInstance.post(`/api/asistencia/aparatos/${aparato.id}/tareas`, { tipo });
            onCambio();
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo pedir la lectura');
        } finally {
            setEnviando(false);
        }
    };

    const cancelar = async () => {
        setEnviando(true);
        try {
            await axiosInstance.post(`/api/asistencia/tareas/${t.id}/cancelar`);
            onCambio();
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo cancelar');
        } finally {
            setEnviando(false);
        }
    };

    const texto = resumen(tipo, t);
    return (
        <div className="min-w-0">
            <div className="flex items-center gap-2">
                {enCurso ? (
                    <>
                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700">
                            <Loader2 size={14} className="animate-spin text-cyan-500" />
                            {ETIQUETAS[tipo].enCurso}
                        </span>
                        <button
                            onClick={cancelar} disabled={enviando}
                            className="text-xs text-slate-500 hover:text-rose-600 disabled:opacity-50 focus-visible:outline-none focus-visible:underline"
                        >
                            Cancelar
                        </button>
                    </>
                ) : (
                    <button
                        onClick={pedir}
                        disabled={enviando || !aparato.conectado}
                        title={aparato.conectado ? undefined : 'El aparato tiene que estar conectado'}
                        className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                    >
                        {ETIQUETAS[tipo].accion}
                    </button>
                )}
            </div>
            {avance != null && (
                <div className="mt-2 h-1 rounded-full bg-slate-100 overflow-hidden max-w-[12rem]">
                    <div className="h-full bg-cyan-400 transition-[width] duration-500" style={{ width: `${avance}%` }} />
                </div>
            )}
            {texto && (
                <p className={`text-xs mt-1.5 ${t.estado === 'error' ? 'text-rose-600' : 'text-slate-500'}`}>{texto}</p>
            )}
        </div>
    );
};
